import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  ChevronLeft, 
  ChevronRight, 
  Plus, 
  Trash2, 
  Users 
} from "lucide-react";
import { format, endOfWeek } from "date-fns";
import { tr } from "date-fns/locale";

import { dayTypeLabels } from './mesaiConstants';

const MesaiTakvimiSection = ({
  // State'ler
  currentWeekStart,
  weekDays,
  // Data
  isBasicRole,
  canEditSchedule = true, // false = gölge admin, çöp ikonu gösterilmez
  // Fonksiyonlar
  goToPreviousWeek,
  goToNextWeek,
  goToCurrentWeek,
  getDayType,
  getSchedulesForDate,
  isSpecialDay,
  getDayBackgroundClass,
  handleDayClick,
  handleScheduleClick,
  openDeleteConfirm
}) => {
  return (
    <div className="space-y-4">
      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-blue-100 border border-blue-300 rounded"></div>
          <span>Hafta Sonu</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-red-100 border border-red-300 rounded"></div>
          <span>Resmi Tatil</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-purple-100 border border-purple-300 rounded"></div>
          <span>İdari Tatil</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-amber-100 border border-dashed border-amber-300 rounded"></div>
          <span>½ Yarım Gün</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-violet-100 border border-violet-300 rounded"></div>
          <span>👷 Formen</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-cyan-100 border border-cyan-300 rounded"></div>
          <span>🔍 Bölge Müh.</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-white border border-gray-300 rounded"></div>
          <span>Normal Gün</span>
        </div>
      </div>

      {/* Week Navigation */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <Button variant="outline" size="sm" onClick={goToPreviousWeek}>
              <ChevronLeft className="h-4 w-4 mr-1" />
              Önceki Hafta
            </Button>
            
            <div className="flex items-center gap-4">
              <h3 className="text-lg font-semibold">
                {format(currentWeekStart, 'd MMMM', { locale: tr })} - {format(endOfWeek(currentWeekStart, { weekStartsOn: 1 }), 'd MMMM yyyy', { locale: tr })}
              </h3>
              <Button variant="outline" size="sm" onClick={goToCurrentWeek}>
                Bu Hafta
              </Button>
            </div>
            
            <Button variant="outline" size="sm" onClick={goToNextWeek}>
              Sonraki Hafta
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Weekly Schedule Grid */}
      <div className="grid grid-cols-7 gap-2">
        {weekDays.map((day, index) => {
          const dayType = getDayType(day);
          const schedules = getSchedulesForDate(day);
          const specialDay = isSpecialDay(day);
          
          return (
            <div
              key={index} 
              className={`min-h-[200px] cursor-pointer rounded-lg border-2 transition-all ${getDayBackgroundClass(day)}`}
              onClick={() => handleDayClick(day)}
            >
              <div className="py-2 px-3 text-center border-b">
                <p className="text-xs text-gray-500">
                  {format(day, 'EEEE', { locale: tr })}
                </p>
                <p className="text-lg font-bold">
                  {format(day, 'd')}
                </p>
                {dayType && (
                  <div className="flex flex-col items-center gap-0.5 mt-1">
                    <Badge
                      variant="secondary"
                      className={`text-xs ${
                        dayType.holidayId && dayType.isFullDay === false ? 'bg-amber-200 text-amber-900' :
                        dayType.type === 'resmi_tatil' ? 'bg-red-200 text-red-800' :
                        dayType.type === 'idari_tatil' ? 'bg-purple-200 text-purple-800' :
                        'bg-blue-200 text-blue-800'
                      }`}
                    >
                      {dayType.type === 'resmi_tatil' || dayType.type === 'idari_tatil'
                        ? dayType.name
                        : dayTypeLabels[dayType.type]}
                    </Badge>
                    {dayType.holidayId && dayType.isFullDay === false && (
                      <span className="text-[10px] font-medium text-amber-700">
                        ½ Yarım Gün
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="px-2 py-1 space-y-1">
                {/* İlk 2 mesaiyi göster, fazlası varsa özet göster */}
                {schedules.slice(0, 2).map(schedule => (
                  <ScheduleItem
                    key={schedule.id}
                    schedule={schedule}
                    day={day}
                    isBasicRole={isBasicRole}
                    canEditSchedule={canEditSchedule}
                    handleScheduleClick={handleScheduleClick}
                    openDeleteConfirm={openDeleteConfirm}
                  />
                ))}
                
                {/* 2'den fazla mesai varsa kompakt özet göster */}
                {schedules.length > 2 && (
                  <div 
                    className="text-center text-xs text-white py-1.5 bg-blue-600 rounded cursor-pointer hover:bg-blue-700 font-medium shadow-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDayClick(day);
                    }}
                  >
                    +{schedules.length - 2} mesai daha
                  </div>
                )}
                
                {/* + Ekle butonu - sadece düzenleme yetkisi olan amirler (gölge admin hariç) */}
                {!isBasicRole && canEditSchedule && (
                  <div 
                    className="text-center text-xs text-green-600 py-2 bg-green-50 rounded cursor-pointer hover:bg-green-100 border border-dashed border-green-300 mt-1"
                    onClick={() => handleDayClick(day)}
                  >
                    <Plus className="h-3 w-3 inline mr-1" />
                    {schedules.length === 0 ? (specialDay ? 'Mesai Ekle' : 'Tatil/Mesai Ekle') : 'Mesai Ekle'}
                  </div>
                )}
                
                {/* Boş gün bilgisi - sadece basic roller için */}
                {isBasicRole && schedules.length === 0 && (
                  <div className="text-center text-xs text-gray-400 py-4">
                    Mesai yok
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Schedule Item alt bileşeni
const ScheduleItem = ({ schedule, day, isBasicRole, canEditSchedule = true, handleScheduleClick, openDeleteConfirm }) => {
  return (
    <div 
      className={`rounded p-2 text-xs border shadow-sm cursor-pointer transition-colors ${
        schedule.cancelled
          ? 'bg-red-50/60 border-red-200 opacity-70'
          : schedule.is_supervisor_schedule
            ? schedule.personnel_role === 'formen'
              ? 'bg-violet-50 hover:bg-violet-100 hover:border-violet-400 border-violet-200'
              : 'bg-cyan-50 hover:bg-cyan-100 hover:border-cyan-400 border-cyan-200'
            : schedule.is_aksam_gece_combined
              ? 'bg-gradient-to-r from-orange-50 to-indigo-50 hover:from-orange-100 hover:to-indigo-100 border-orange-200'
              : schedule.schedule_type === 'mobile' 
                ? 'bg-purple-50 hover:bg-purple-100 hover:border-purple-400 border-purple-200' 
                : 'bg-white hover:bg-green-50 hover:border-green-300'
      }`}
      onClick={(e) => {
        e.stopPropagation();
        handleScheduleClick(schedule, day);
      }}
    >
      <div className="flex items-center justify-between mb-1">
        <span className={`font-semibold truncate ${
          schedule.cancelled ? 'line-through text-gray-400' :
          schedule.is_supervisor_schedule
            ? schedule.personnel_role === 'formen' ? 'text-violet-700' : 'text-cyan-700'
            : schedule.is_aksam_gece_combined ? 'text-orange-700' 
            : schedule.schedule_type === 'mobile' ? 'text-purple-700' : 'text-green-700'
        }`}>
          {schedule.is_supervisor_schedule && (schedule.personnel_role === 'formen' ? '👷 ' : '🔍 ')}
          {schedule.is_aksam_gece_combined && '🌙 '}
          {schedule.schedule_type === 'mobile' && '🚐 '}
          {schedule.is_supervisor_schedule ? schedule.personnel_name : schedule.park_name}
        </span>
        {/* İptal edilmişse İPTAL etiketi göster */}
        {schedule.cancelled && (
          <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 bg-red-100 text-red-600 border-red-300 shrink-0">
            İPTAL
          </Badge>
        )}
        {/* Silme butonu - sadece düzenleme yetkisi olan amirler (gölge admin hariç), iptal edilmemişse */}
        {canEditSchedule && !isBasicRole && !schedule.cancelled && !schedule.is_aksam_gece_combined && (
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-5 w-5 p-0 hover:bg-red-100 shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              if (schedule.is_supervisor_schedule) {
                openDeleteConfirm('supervisor_overtime', schedule.id, schedule.personnel_name);
              } else {
                openDeleteConfirm('duty', schedule.id, schedule.park_name);
              }
            }}
          >
            <Trash2 className="h-3 w-3 text-red-500" />
          </Button>
        )}
      </div>
      {/* Supervisor için rol etiketi */}
      {schedule.is_supervisor_schedule && (
        <div className="mb-1">
          <Badge 
            variant="secondary" 
            className={`text-[9px] ${
              schedule.cancelled ? 'bg-gray-200 text-gray-500' :
              schedule.personnel_role === 'formen' 
                ? 'bg-violet-200 text-violet-800' 
                : 'bg-cyan-200 text-cyan-800'
            }`}
          >
            {schedule.personnel_role === 'formen' ? 'Formen - Alan Denetimi' : 'BM - Bölge Denetimi'}
          </Badge>
        </div>
      )}
      {/* Akşam/Gece mesaisi için özel gösterim */}
      {schedule.is_aksam_gece_combined && (
        <div className="text-[10px] text-gray-600 mb-1">
          <span className="text-orange-600">{schedule.aksam_hours}s akşam</span>
          {schedule.aksam_hours > 0 && schedule.gece_hours > 0 && ' + '}
          <span className="text-indigo-600">{schedule.gece_hours}s gece</span>
          <span className="text-gray-500 ml-1">({schedule.display_time})</span>
        </div>
      )}
      {/* Normal mesai personel listesi */}
      {!schedule.is_supervisor_schedule && (
        <div className="text-gray-600">
          {schedule.assigned_personnel?.slice(0, 2).map(p => (
            <div key={p.personnel_id} className={`flex items-center gap-1 ${p.cancelled ? 'opacity-50' : ''}`}>
              <Users className={`h-3 w-3 ${p.cancelled ? 'text-red-400' : ''}`} />
              <span className={`truncate ${p.cancelled ? 'line-through text-red-500' : ''}`}>
                {p.personnel_name}
              </span>
              {p.cancelled && (
                <Badge variant="outline" className="text-[9px] px-1 py-0 h-3 bg-red-100 text-red-600 border-red-300">
                  İPTAL
                </Badge>
              )}
            </div>
          ))}
          {schedule.assigned_personnel?.length > 2 && (
            <div className="text-[10px] text-gray-500 mt-1">
              +{schedule.assigned_personnel.length - 2} kişi daha
            </div>
          )}
        </div>
      )}
      {/* Mobil ekip formen bilgisi */}
      {schedule.schedule_type === 'mobile' && schedule.formen_name && (
        <div className="mt-1 text-[10px] text-purple-600">
          👤 {schedule.formen_name} 
          {schedule.formen_joining === false && (
            <span className="text-orange-600"> (Katılmıyor)</span>
          )}
        </div>
      )}
    </div>
  );
};

export default MesaiTakvimiSection;
